import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { Button } from 'antd'

import {
  BarChartOutlined,
  DatabaseOutlined,
  FontSizeOutlined,
  LeftOutlined,
  RightOutlined,
  TableOutlined
} from '@ant-design/icons'

import { selectDataSources } from '@/store/slices/data/selectors'
import { selectAllSlideIds, selectCurrentSlideId } from '@/store/slices/slides/selectors'
import { selectCanvasSelectedObject } from '@store/slices/shapes/selectors'

type Props = {
  dataTableId: number
}

const shapeIcons = {
  table: <TableOutlined />,
  textbox: <FontSizeOutlined />
}

const WorkbenchHeader = ({ dataTableId }: Props) => {
  const history = useHistory()
  const selectedObject = useSelector(selectCanvasSelectedObject)
  const dataSources = useSelector(selectDataSources)
  const slideIds = useSelector(selectAllSlideIds)
  const currentSlideId = useSelector(selectCurrentSlideId)

  const breadcrumb = useMemo(() => {
    for (const source of dataSources) {
      for (const section of source.dataSections || []) {
        const table = section.dataTables?.find(t => t.id === dataTableId)
        if (table) {
          return { source: source.filename, section: section.name, table: table.name }
        }
      }
    }
    return null
  }, [dataSources, dataTableId])

  const slideNumber = slideIds.indexOf(selectedObject?.slide ?? currentSlideId) + 1

  return (
    <header className="workbench-header">
      <Button icon={<LeftOutlined />} onClick={() => history.goBack()}>
        Back to slides
      </Button>
      <span className="header-divider" />
      <nav className="breadcrumb">
        {breadcrumb ? (
          <>
            <DatabaseOutlined />
            <span>{breadcrumb.source}</span>
            <RightOutlined className="chevron" />
            <span>{breadcrumb.section}</span>
            <RightOutlined className="chevron" />
            <span className="current">{breadcrumb.table}</span>
          </>
        ) : (
          <span>No table selected</span>
        )}
      </nav>
      {selectedObject?.name && slideNumber > 0 && (
        <span className="shape-chip">
          {shapeIcons[selectedObject.type] || <BarChartOutlined />}
          {selectedObject.name} · Slide {slideNumber}
        </span>
      )}
    </header>
  )
}

export default WorkbenchHeader
